import { useEffect, useRef, useState } from 'react';
import {
  HERO_ANSWER_DELAY_MS,
  HERO_ANSWER_HOLD_MS,
  HERO_FADE_DURATION_MS,
  HERO_QUESTION_DELAY_MS,
  HERO_THINKING_DELAY_MS,
  HERO_TYPING_CHUNK_SIZE,
  HERO_TYPING_START_DELAY_MS,
  HERO_TYPING_STEP_MS,
} from './constants';

export default function HeroChatPreview({ scenarioPreviews, onAskQuestion }) {
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [phase, setPhase] = useState(0);
  const [fadingOut, setFadingOut] = useState(false);
  const [typedChars, setTypedChars] = useState(0);
  const timers = useRef([]);
  const scenario = scenarioPreviews[scenarioIndex] || scenarioPreviews[0];

  function clearAll() {
    timers.current.forEach((timerId) => {
      window.clearTimeout(timerId);
      window.clearInterval(timerId);
    });
    timers.current = [];
  }

  function scheduleTyping(startCount = 0) {
    if (!scenario?.question || startCount >= scenario.question.length) return null;

    const intervalId = window.setInterval(() => {
      setTypedChars((count) => {
        const next = Math.min(
          scenario.question.length,
          Math.max(count, startCount) + HERO_TYPING_CHUNK_SIZE,
        );
        if (next >= scenario.question.length) {
          window.clearInterval(intervalId);
        }
        return next;
      });
    }, HERO_TYPING_STEP_MS);

    return intervalId;
  }

  function startScenario() {
    clearAll();
    setPhase(0);
    setFadingOut(false);
    setTypedChars(0);

    const typingDelayTimer = window.setTimeout(() => {
      const typingTimer = scheduleTyping(0);
      if (typingTimer) {
        timers.current.push(typingTimer);
      }
    }, HERO_TYPING_START_DELAY_MS);

    timers.current = [
      typingDelayTimer,
      window.setTimeout(() => setPhase(1), HERO_QUESTION_DELAY_MS),
      window.setTimeout(() => setPhase(2), HERO_THINKING_DELAY_MS),
      window.setTimeout(() => setPhase(3), HERO_ANSWER_DELAY_MS),
    ];
  }

  useEffect(() => {
    if (!scenario) return undefined;
    startScenario();
    return clearAll;
  }, [scenarioIndex, scenario?.question]);

  useEffect(() => {
    if (phase !== 3 || scenarioPreviews.length <= 1) return undefined;

    const holdTimer = window.setTimeout(() => {
      setFadingOut(true);
      const fadeTimer = window.setTimeout(() => {
        setFadingOut(false);
        setScenarioIndex((current) => (current + 1) % scenarioPreviews.length);
      }, HERO_FADE_DURATION_MS);
      timers.current.push(fadeTimer);
    }, HERO_ANSWER_HOLD_MS);
    timers.current.push(holdTimer);

    return () => window.clearTimeout(holdTimer);
  }, [phase, scenarioPreviews.length]);

  function handleDotClick(index) {
    if (index === scenarioIndex || fadingOut) return;
    clearAll();
    setFadingOut(true);
    const fadeTimer = window.setTimeout(() => {
      setFadingOut(false);
      setScenarioIndex(index);
    }, HERO_FADE_DURATION_MS);
    timers.current = [fadeTimer];
  }

  if (!scenario) return null;

  const isRisk = scenario.isRisk;
  const memberTone = isRisk ? 'bg-tf-terracotta-mid/35 text-tf-ink' : 'bg-tf-teal/10 text-tf-teal';
  const questionShown = phase >= 1;
  const answerShown = phase >= 2;
  const questionOpacity = fadingOut
    ? 'opacity-0'
    : phase >= 3
      ? 'opacity-[0.22]'
      : phase >= 2
        ? 'opacity-80'
        : phase >= 1
          ? 'opacity-100'
          : 'opacity-0';
  const answerOpacity = fadingOut
    ? 'opacity-0'
    : phase >= 3
      ? 'opacity-100'
      : phase >= 2
        ? 'opacity-100'
        : 'opacity-0';
  const questionHeightClass = !questionShown
    ? 'max-h-0'
    : 'max-h-[126px] pb-3 sm:max-h-[138px] sm:pb-4';
  const composerText = phase === 0 ? scenario.question.slice(0, typedChars) : '';
  const composerTextTone = composerText ? 'text-tf-ink' : 'text-tf-ink/40';
  const answerParagraphs =
    Array.isArray(scenario.answerParagraphs) && scenario.answerParagraphs.length > 0
      ? scenario.answerParagraphs
      : [scenario.answer];

  return (
    <div className="mx-auto flex w-full max-w-[24rem] flex-col lg:max-w-[31rem] lg:-translate-x-3 lg:justify-self-end">
      <div className="relative overflow-hidden rounded-2xl border border-tf-stone bg-tf-surface p-4 shadow-[0_24px_60px_-40px_rgba(16,16,16,0.25)] lg:overflow-visible lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none">
        <div className="flex h-[350px] flex-col justify-end overflow-hidden pb-[7.75rem] sm:h-[396px] sm:pb-[8rem] lg:h-[392px] lg:pb-[6.9rem]">
          <div className={`overflow-hidden transition-all duration-700 ${questionHeightClass}`}>
            <div className={`flex justify-end transition-opacity duration-700 ${questionOpacity}`}>
              <div className="max-w-[86%] sm:max-w-[74%] lg:max-w-[72%]">
                <div className="mb-1.5 flex items-center justify-end gap-2">
                  <div className="text-right">
                    <span className="text-[10px] font-medium text-tf-ink/80 sm:text-[11px]">
                      {scenario.displayName}
                    </span>
                    <span className="text-[10px] font-light text-tf-ink/55 sm:text-[11px]">
                      {' '}
                      · {scenario.hint}
                    </span>
                  </div>
                  <div
                    className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-semibold shadow-sm ${memberTone}`}
                  >
                    {scenario.avatarText || scenario.firstName.slice(0, 2).toUpperCase()}
                  </div>
                </div>
                <div className="rounded-[22px] rounded-tr-sm border border-tf-stone bg-white px-4 py-3 shadow-[0_2px_12px_-2px_rgba(16,16,16,0.06)]">
                  <p className="text-[13.5px] font-light leading-snug text-tf-ink sm:text-[14px]">
                    {scenario.question}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`overflow-hidden transition-all duration-700 ${answerShown ? 'max-h-[210px] pb-4 sm:max-h-[220px] sm:pb-5' : 'max-h-0'}`}
          >
            <div className={`flex justify-start transition-opacity duration-700 ${answerOpacity}`}>
              <div className="max-w-[88%] sm:max-w-[78%] lg:max-w-[76%]">
                <div className="mb-1.5 flex items-center gap-1.5">
                  <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-tf-teal text-[9px] font-medium text-white">
                    TF
                  </div>
                  <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-tf-teal">
                    ThoughtFabric Concierge
                  </p>
                </div>
                <div className="rounded-[22px] rounded-tl-sm border border-tf-stone bg-white px-4 py-3 shadow-[0_2px_12px_-2px_rgba(16,16,16,0.06)]">
                  <div className="relative">
                    <div
                      className={`absolute left-0 top-0 flex items-center gap-1 transition-opacity duration-500 ${phase === 2 ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-tf-ink/25 animate-pulse" />
                      <span className="h-1.5 w-1.5 rounded-full bg-tf-ink/25 animate-pulse [animation-delay:0.2s]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-tf-ink/25 animate-pulse [animation-delay:0.4s]" />
                    </div>
                    <div
                      className={`transition-opacity duration-700 ${phase >= 3 ? 'opacity-100' : 'opacity-0'}`}
                    >
                      {answerParagraphs.map((paragraph, index) => (
                        <p
                          key={`${scenario.key}-answer-${index + 1}`}
                          className={`text-[13px] font-light leading-[1.65] text-tf-ink/85 sm:text-[13.5px] ${index > 0 ? 'mt-2' : ''}`}
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute inset-x-4 bottom-4">
          <div className="flex items-center gap-2 rounded-[18px] border border-tf-stone bg-white px-3.5 py-3 shadow-[0_18px_36px_-28px_rgba(16,16,16,0.18)]">
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-tf-teal/10 text-tf-teal-mid">
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
                />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 3v4" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M22 5h-4" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 17v2" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 18H3" />
              </svg>
            </div>
            <span
              className={`flex min-h-[1rem] flex-1 items-center text-[12px] leading-none sm:text-[12.5px] ${composerTextTone}`}
            >
              <span>{composerText || 'Ask about your membership…'}</span>
            </span>
            <button
              type="button"
              onClick={() => onAskQuestion(scenario.question, scenario.scenarioCode)}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-tf-teal text-white transition-colors hover:bg-tf-teal-mid"
              aria-label="Ask this question"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 12h14m-6-6 6 6-6 6"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2 px-4">
        {scenarioPreviews.map((item, index) => (
          <button
            key={item.key}
            type="button"
            onClick={() => handleDotClick(index)}
            aria-label={`Scenario ${index + 1}`}
            className={`rounded-full transition-all duration-500 ${
              index === scenarioIndex
                ? 'h-2 w-4 bg-tf-teal-mid'
                : 'h-2 w-2 bg-tf-ink/15 hover:bg-tf-ink/35'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
